import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import { Close as CloseIcon } from '@material-ui/icons'
import { Autocomplete } from '@material-ui/lab'
import ConfirmDialog from 'components/ConfirmDialog'
import { State } from 'containers/Dashboard/types'
import useStates from 'hooks/useStates'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

import './styles.scss'

interface AddStateDialogProps {
  open: boolean
  handleClose: () => void
}

const AddStateDialog: React.FC<AddStateDialogProps> = ({
  open,
  handleClose,
}) => {
  const { states, eligibleStates, handleUpdateStateEligibility } = useStates()
  const [values, setValues] = useState<State[]>(eligibleStates)
  const [confirmAddText, setConfirmAddText] = useState<string | null>(null)

  const newStates = useMemo(
    () => values.filter((state) => eligibleStates.indexOf(state) === -1),
    [values, eligibleStates]
  )

  useEffect(() => {
    setValues(eligibleStates)
  }, [eligibleStates, setValues])

  const handleChange = useCallback(
    (_: React.ChangeEvent<unknown>, value: State[]) => {
      setValues([
        ...eligibleStates,
        ...value.filter((state) => eligibleStates.indexOf(state) === -1),
      ])
    },
    [eligibleStates]
  )

  const openConfirmDialog = () =>
    setConfirmAddText(
      `state${newStates.length > 1 ? 's' : ''} ${newStates
        .map((s) => s.fullName)
        .join(', ')}`
    )

  const closeConfirmDialog = () => setConfirmAddText(null)

  const handleCancel = () => {
    setValues(eligibleStates)
    handleClose()
  }

  const handleSave = useCallback(() => {
    newStates.forEach((s) =>
      handleUpdateStateEligibility({ state: s, eligible: true })
    )

    // TODO: add success confirmation
    handleClose()
  }, [newStates, handleClose])

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      aria-labelledby="add-state-dialog"
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          className="close-button"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="h4">Add State</Typography>
        <div className="add-state-list">
          <Autocomplete
            multiple
            autoHighlight
            options={states}
            getOptionLabel={(option) => option.fullName}
            getOptionSelected={(option, value) => option.code === value.code}
            defaultValue={values}
            onChange={handleChange}
            filterSelectedOptions
            renderTags={(tagValue) =>
              tagValue.map((state) => (
                <Chip
                  key={state.code}
                  label={state.fullName}
                  className="state-chip"
                  disabled={eligibleStates.includes(state)}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label="States"
                placeholder="Enter State name..."
              />
            )}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={openConfirmDialog} disabled={newStates.length < 1}>
          Save
        </Button>
      </DialogActions>
      <ConfirmDialog
        key={newStates.map((s) => s.code).join('-')}
        open={confirmAddText !== null}
        text={`Are you sure you want to add ${confirmAddText}?`}
        titleText={`Confirm Add Eligible State${
          newStates.length > 1 ? 's' : ''
        }?`}
        helperText={'Note: this action cannot be undone via the admin panel'}
        onConfirm={handleSave}
        handleClose={closeConfirmDialog}
      />
    </Dialog>
  )
}

export default memo(AddStateDialog)
